// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { getAppAdminFeatures } from 'src/helpers/features-helper';
import { checkSelectThemeOS20 } from 'src/helpers/theme-setup-helper';

import apiService from 'src/services/api-service';
import {
  GET_THEMES_REQUEST,
  GET_THEMES_SUCCESS,
  GET_THEMES_FAILURE,
  GET_ALL_THEMES_REQUEST,
  GET_ALL_THEMES_SUCCESS,
  GET_ALL_THEMES_FAILURE,
  CHECK_THEME_REQUEST,
  CHECK_THEME_SUCCESS,
  CHECK_THEME_FAILURE,
  SETUP_THEME_REQUEST,
  SETUP_THEME_SUCCESS,
  SETUP_THEME_FAILURE,
  SUBMIT_ERROR_REQUEST,
  SUBMIT_ERROR_SUCCESS,
  SUBMIT_ERROR_FAILURE,
  INTEGRATE_GEMPAGES_REQUEST,
  INTEGRATE_GEMPAGES_SUCCESS,
  INTEGRATE_GEMPAGES_FAILURE,
  RESET_THEME_SETUP
} from 'stores/flow-type/theme-setup.type';

/**
 * call to get list of installed themes api
 * @return {object} The RSAA object for API middleware
 */
export const getThemes = (i18n: I18n, controlledFetch = false) => {
  return apiService.sendRequest('admin-v3.themes.get-themes', {}, [
    {
      type: GET_THEMES_REQUEST,
      meta: controlledFetch
        ? {
            controlledFetch,
            dataStatePath: 'themeSetup.themes',
            loadingStatePath: 'themeSetup.getThemesLoading'
          }
        : undefined
    },
    {
      type: GET_THEMES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_THEMES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.getThemes'),
          i18n
        )
    }
  ]);
};

/**
 * call to get list of all themes api
 * @return {object} The RSAA object for API middleware
 */
export const getAllThemes = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.theme-setup.list-themes', {}, [
    GET_ALL_THEMES_REQUEST,
    {
      type: GET_ALL_THEMES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_ALL_THEMES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.getAllThemes'),
          i18n
        )
    }
  ]);
};

/**
 * call to check theme for install api
 * @return {object} The RSAA object for API middleware
 */
export const checkTheme = (themeId: string, i18n: I18n) => {
  const themeSetupV2 = getAppAdminFeatures('themeSetupV2');

  const route = themeSetupV2
    ? 'admin-v3.theme-setup.theme-lib-for-install'
    : 'admin-v2.setup-requests.theme-lib-for-install';

  const params = { themeId };
  return apiService.sendRequest(route, { params }, [
    CHECK_THEME_REQUEST,
    {
      type: CHECK_THEME_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: CHECK_THEME_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.checkTheme'),
          i18n
        )
    }
  ]);
};

/**
 * call to install theme api
 * @return {object} The RSAA object for API middleware
 */
export const setupTheme = (data, meta: Object, i18n: I18n) => {
  const body = { requestParams: data };
  const themeSetupV2 = getAppAdminFeatures('themeSetupV2');
  const isOS20 = checkSelectThemeOS20();

  // Theme setup v2 OR
  // Theme setup v1 and install theme OS 2.0
  // => Call install api v2
  const route =
    themeSetupV2 || (!themeSetupV2 && isOS20)
      ? 'admin-v3.theme-setup.install-theme'
      : 'admin-v2.setup-requests.install-theme';

  return apiService.sendRequest(route, { body }, [
    SETUP_THEME_REQUEST,
    {
      type: SETUP_THEME_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      },
      meta
    },
    {
      type: SETUP_THEME_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.setupTheme'),
          i18n
        );

        return errorResult;
      },
      meta
    }
  ]);
};

/**
 * @deprecated
 * call to submit error api
 * @return {object} The RSAA object for API middleware
 */
export const submitError = (email, i18n: I18n) => {
  const body = { email };
  return apiService.sendRequest('admin-v2.setup-requests.submit-error', { body }, [
    SUBMIT_ERROR_REQUEST,
    {
      type: SUBMIT_ERROR_SUCCESS,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'info',
          i18n.translate('AdminHTML.ThemeSetup.Actions.submitError_Success'),
          i18n
        )
    },
    {
      type: SUBMIT_ERROR_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.submitError_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to reset state
 * @return {object} The RSAA object for API middleware
 */
export const unmountComponent = () => {
  return { type: RESET_THEME_SETUP };
};

/**
 * call to install theme api
 * @return {object} The RSAA object for API middleware
 */
export const integrateGemPages = (i18n: I18n) => {
  const route = 'admin-v3.theme-setup.integrate-gempages';

  return apiService.sendRequest(route, {}, [
    INTEGRATE_GEMPAGES_REQUEST,
    {
      type: INTEGRATE_GEMPAGES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'info',
          i18n.translate('AdminHTML.ThemeSetup.Actions.integrateGemPages_Success'),
          i18n
        );

        return { data };
      }
    },
    {
      type: INTEGRATE_GEMPAGES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.ThemeSetup.Actions.integrateGemPages_Failure'),
          i18n
        )
    }
  ]);
};
